import { CheckCircle2, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { WizardLayout } from "@/components/layout/wizard-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NIS2_EVALUATION_STEPS } from "@/constants";
import { LOADING_DURATIONS } from "@/config/loadingDurations";

type EvaluationLoadingScreenProps = {
  currentStep: number;
  steps: string[];
  onBack?: () => void;
  onStepClick?: (stepIndex: number) => void;
  userAvatar?: React.ReactNode;
};

export function EvaluationLoadingScreen({
  currentStep,
  steps,
  onBack,
  onStepClick,
  userAvatar,
}: EvaluationLoadingScreenProps) {
  const [evaluationStep, setEvaluationStep] = useState(0);
  const evaluationSteps = [...NIS2_EVALUATION_STEPS, "Finalizare clasificare"];
  const STEP_INTERVAL_MS = Math.floor(
    LOADING_DURATIONS.nis2EvalStepMs / (evaluationSteps.length + 1)
  );

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    for (let i = 0; i <= evaluationSteps.length; i++) {
      const timer = setTimeout(() => {
        setEvaluationStep(i);
      }, i * LOADING_DURATIONS.nis2EvalStepMs);
      timers.push(timer);
    }

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [evaluationSteps.length, STEP_INTERVAL_MS]);

  return (
    <WizardLayout
      title="Clasificare NIS 2 - Aplicare Algoritm DNSC"
      description="Se aplică algoritmul oficial de clasificare pe baza celor trei piloni decizionali."
      currentStep={currentStep}
      steps={steps}
      onBack={onBack}
      onStepClick={onStepClick}
      showBackButton
      userAvatar={userAvatar}
    >
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-primary animate-pulse" />
              Evaluare în curs...
            </CardTitle>
            <CardDescription>
              Se analizează entitatea conform algoritmului DNSC
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  Progres evaluare:{" "}
                  {Math.min(evaluationStep, evaluationSteps.length)}/
                  {evaluationSteps.length}
                </span>
                <span className="text-sm text-muted-foreground">
                  {Math.round(
                    (Math.min(evaluationStep, evaluationSteps.length) /
                      evaluationSteps.length) *
                      100
                  )}
                  %
                </span>
              </div>
              <Progress
                value={
                  (Math.min(evaluationStep, evaluationSteps.length) /
                    evaluationSteps.length) *
                  100
                }
                className="h-2"
              />
            </div>

            <ScrollArea className="w-full border rounded-md p-4">
              <div className="space-y-2">
                {evaluationSteps.map((step, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-2 rounded transition-colors ${
                      index < evaluationStep
                        ? "bg-success/10 text-success"
                        : index === evaluationStep
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground"
                    }`}
                  >
                    {index < evaluationStep ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : index === evaluationStep ? (
                      <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="h-4 w-4 border-2 border-muted-foreground/30 rounded-full" />
                    )}
                    <span className="text-sm font-medium">{step}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </WizardLayout>
  );
}
